import { ref } from 'vue'
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'
import { getFirestore, collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp, updateDoc } from 'firebase/firestore'
import { useCompanyStore } from '../stores/company'
import { useAuthStore } from '../stores/auth'
import { usePOChangeLog } from './usePOChangeLog'
import { resolveMimeType } from '../utils/mimeType'

export interface PurchaseOrderDocument {
  id: string
  purchaseOrderId: string
  fileName: string
  fileUrl: string
  mimeType: string
  fileType: string  // Tipo de documento (commercial_invoice, packing_list, etc.)
  fileSize: number
  uploadedBy: string
  uploadedAt: any
  storagePath: string
  folderId?: string | null
  extractedData?: Record<string, any>
  reviewedBy?: {
    uid: string
    name: string
    lastName: string
    reviewedAt: any
  } | null
}

export interface UploadProgress {
  fileName: string
  progress: number
  status: 'uploading' | 'completed' | 'error'
  error?: string
}

export function usePurchaseOrderDocuments() {
  const companyStore = useCompanyStore()
  const authStore = useAuthStore()
  const db = getFirestore()
  const storage = getStorage()

  const { logDocumentUploaded, logDocumentDeleted, logDocumentTypeChanged } = usePOChangeLog()

  const documents = ref<PurchaseOrderDocument[]>([])
  const isLoading = ref(false)
  const isUploading = ref(false)
  const uploadProgress = ref<UploadProgress | null>(null)
  const error = ref<string | null>(null)

  /**
   * Cargar documentos de una orden de compra
   */
  const loadDocuments = async (purchaseOrderId: string) => {
    try {
      isLoading.value = true
      error.value = null
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      const docsRef = collection(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents`)
      const q = query(docsRef, orderBy('uploadedAt', 'desc'))
      const snapshot = await getDocs(q)

      documents.value = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          mimeType: resolveMimeType(data.mimeType, data.fileName)
        }
      }) as PurchaseOrderDocument[]

    } catch (err) {
      console.error('Error loading documents:', err)
      error.value = err instanceof Error ? err.message : 'Error al cargar documentos'
      documents.value = []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Subir archivo a Firebase Storage y guardar metadata en Firestore
   */
  const moveDocumentToFolder = async (
    documentId: string,
    purchaseOrderId: string,
    targetFolderId: string | null
  ) => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      const docRef = doc(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents/${documentId}`)
      await updateDoc(docRef, { folderId: targetFolderId })

      const docIndex = documents.value.findIndex(d => d.id === documentId)
      if (docIndex !== -1) {
        documents.value[docIndex].folderId = targetFolderId
      }
    } catch (err) {
      console.error('Error moving document to folder:', err)
      error.value = err instanceof Error ? err.message : 'Error al mover documento'
      throw err
    }
  }

  const uploadDocument = async (
    file: File,
    purchaseOrderId: string,
    documentType: string = 'other',
    folderId?: string | null
  ): Promise<PurchaseOrderDocument | null> => {
    try {
      isUploading.value = true
      error.value = null
      const companyId = companyStore.selectedCompanyId
      const userId = authStore.user?.uid || 'unknown'

      if (!companyId) throw new Error('No company selected')

      // Generar path único en Storage
      const timestamp = Date.now()
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const storagePath = `companies/${companyId}/purchaseOrders/${purchaseOrderId}/documents/${timestamp}_${sanitizedFileName}`

      // Referencia en Storage
      const fileRef = storageRef(storage, storagePath)

      // Inicializar progreso
      uploadProgress.value = {
        fileName: file.name,
        progress: 0,
        status: 'uploading'
      }

      // Subir archivo
      const uploadTask = uploadBytesResumable(fileRef, file)

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          // Progress
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            uploadProgress.value = {
              fileName: file.name,
              progress: Math.round(progress),
              status: 'uploading'
            }
          },
          // Error
          (err) => {
            uploadProgress.value = {
              fileName: file.name,
              progress: 0,
              status: 'error',
              error: err.message
            }
            error.value = err.message
            reject(err)
          },
          // Complete
          async () => {
            try {
              // Obtener URL de descarga
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)

              // Obtener información del usuario
              const userName = authStore.user?.name && authStore.user?.lastName
                ? `${authStore.user.name} ${authStore.user.lastName}`.trim()
                : authStore.user?.email || 'Usuario'

              // Guardar metadata en Firestore
              const documentData: Record<string, any> = {
                purchaseOrderId,
                fileName: file.name,
                fileUrl: downloadUrl,
                mimeType: file.type || getFileType(file.name),
                fileType: documentType,
                fileSize: file.size,
                uploadedBy: userName,
                uploadedAt: serverTimestamp(),
                storagePath,
                createdBy: userId
              }
              if (folderId) {
                documentData.folderId = folderId
              }

              const docsRef = collection(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents`)
              const docRef = await addDoc(docsRef, documentData)

              const newDocument: PurchaseOrderDocument = {
                id: docRef.id,
                purchaseOrderId,
                fileName: file.name,
                fileUrl: downloadUrl,
                mimeType: file.type || getFileType(file.name),
                fileType: documentType,
                fileSize: file.size,
                uploadedBy: userName,
                uploadedAt: new Date(),
                storagePath,
                folderId: folderId || null
              }

              // Agregar a la lista local
              documents.value.unshift(newDocument)

              uploadProgress.value = {
                fileName: file.name,
                progress: 100,
                status: 'completed'
              }

              // Registrar en changeLogs
              await logDocumentUploaded(purchaseOrderId, file.name, documentType)

              resolve(newDocument)
            } catch (firestoreError) {
              uploadProgress.value = {
                fileName: file.name,
                progress: 0,
                status: 'error',
                error: 'Error al guardar metadata'
              }
              error.value = 'Error al guardar metadata del documento'
              reject(firestoreError)
            }
          }
        )
      })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al subir archivo'
      uploadProgress.value = {
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: error.value
      }
      return null
    } finally {
      isUploading.value = false
      // Limpiar progreso después de 3 segundos
      setTimeout(() => {
        uploadProgress.value = null
      }, 3000)
    }
  }

  /**
   * Eliminar documento
   */
  const deleteDocument = async (documentId: string, purchaseOrderId: string) => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      // Buscar documento
      const document = documents.value.find(d => d.id === documentId)
      if (!document) throw new Error('Documento no encontrado')

      // Eliminar de Storage (si el archivo ya no existe, continuar con la eliminación del documento)
      const fileRef = storageRef(storage, document.storagePath)
      try {
        await deleteObject(fileRef)
      } catch (storageError: any) {
        if (storageError?.code === 'storage/object-not-found') {
          console.warn(`⚠️ Archivo "${document.fileName}" no encontrado en Storage, continuando con eliminación del documento en Firestore`)
        } else {
          throw storageError
        }
      }

      // Eliminar de Firestore
      const docRef = doc(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents/${documentId}`)
      await deleteDoc(docRef)

      // Remover de lista local
      documents.value = documents.value.filter(d => d.id !== documentId)

      // Registrar en changeLogs
      await logDocumentDeleted(purchaseOrderId, document.fileName)

    } catch (err) {
      console.error('Error deleting document:', err)
      error.value = err instanceof Error ? err.message : 'Error al eliminar documento'
      throw err
    }
  }

  /**
   * Descargar documento
   */
  const downloadDocument = (document: PurchaseOrderDocument) => {
    const link = window.document.createElement('a')
    link.href = document.fileUrl
    link.download = document.fileName
    link.target = '_blank'
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
  }

  /**
   * Actualizar tipo de documento
   */
  const updateDocumentType = async (
    documentId: string,
    purchaseOrderId: string,
    newType: string
  ) => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      // Capturar valores antes de actualizar
      const currentDoc = documents.value.find(d => d.id === documentId)
      const oldType = currentDoc?.fileType || 'unknown'
      const docFileName = currentDoc?.fileName || documentId

      const docRef = doc(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents/${documentId}`)
      await updateDoc(docRef, {
        fileType: newType
      })

      // Actualizar en la lista local
      const docIndex = documents.value.findIndex(d => d.id === documentId)
      if (docIndex !== -1) {
        documents.value[docIndex].fileType = newType
      }

      // Registrar en changeLogs
      await logDocumentTypeChanged(purchaseOrderId, docFileName, oldType, newType)

    } catch (err) {
      console.error('Error updating document type:', err)
      error.value = err instanceof Error ? err.message : 'Error al actualizar tipo de documento'
      throw err
    }
  }

  /**
   * Copiar referencias de documentos específicos de una OC madre a OCs hijas seleccionadas.
   * No duplica el archivo en Storage, solo copia la metadata en Firestore.
   * Retorna un resumen con documentos copiados y duplicados encontrados.
   */
  const copyDocumentsToChildren = async (
    parentPurchaseOrderId: string,
    childPurchaseOrderIds: string[],
    documentIds: string[]
  ): Promise<{ copied: number; duplicates: Array<{ childId: string; fileName: string }> }> => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No company selected')

    const userId = authStore.user?.uid || 'unknown'
    const userName = authStore.user?.name && authStore.user?.lastName
      ? `${authStore.user.name} ${authStore.user.lastName}`.trim()
      : authStore.user?.email || 'Usuario'

    // Cargar documentos de la OC madre y filtrar solo los seleccionados
    const parentDocsRef = collection(db, `Companies/${companyId}/purchaseOrders/${parentPurchaseOrderId}/Documents`)
    const parentSnapshot = await getDocs(parentDocsRef)
    const documentIdsSet = new Set(documentIds)
    const parentDocs = parentSnapshot.docs
      .filter(d => documentIdsSet.has(d.id))
      .map(d => ({ id: d.id, ...d.data() }))

    if (parentDocs.length === 0) throw new Error('No se encontraron los documentos seleccionados')

    let copied = 0
    const duplicates: Array<{ childId: string; fileName: string }> = []

    for (const childId of childPurchaseOrderIds) {
      // Cargar documentos existentes de la OC hija para detectar duplicados
      const childDocsRef = collection(db, `Companies/${companyId}/purchaseOrders/${childId}/Documents`)
      const childSnapshot = await getDocs(childDocsRef)
      const existingFileNames = new Set(childSnapshot.docs.map(d => d.data().fileName))

      for (const parentDoc of parentDocs) {
        const docData = parentDoc as Record<string, any>

        if (existingFileNames.has(docData.fileName)) {
          duplicates.push({ childId, fileName: docData.fileName })
          continue
        }

        // Copiar referencia del documento (misma URL y storagePath, nueva metadata)
        await addDoc(childDocsRef, {
          purchaseOrderId: childId,
          fileName: docData.fileName,
          fileUrl: docData.fileUrl,
          mimeType: docData.mimeType,
          fileType: docData.fileType,
          fileSize: docData.fileSize,
          storagePath: docData.storagePath,
          uploadedBy: docData.uploadedBy,
          uploadedAt: serverTimestamp(),
          createdBy: userId,
          copiedFrom: parentPurchaseOrderId,
          copiedBy: userName
        })
        copied++
      }
    }

    return { copied, duplicates }
  }

  /**
   * Limpiar progreso
   */
  const clearProgress = () => {
    uploadProgress.value = null
  }

  // Helper: determinar tipo de archivo por extensión
  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif'
    }
    return mimeTypes[extension || ''] || 'application/octet-stream'
  }

  const markDocumentReviewed = async (documentId: string, purchaseOrderId: string) => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')
      if (!authStore.user) throw new Error('No user authenticated')

      const reviewer = {
        uid: authStore.user.uid,
        name: authStore.user.name,
        lastName: authStore.user.lastName,
        reviewedAt: new Date()
      }

      const docRef = doc(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents/${documentId}`)
      await updateDoc(docRef, { reviewedBy: reviewer })

      const docIndex = documents.value.findIndex(d => d.id === documentId)
      if (docIndex !== -1) {
        documents.value[docIndex].reviewedBy = reviewer
      }
    } catch (err) {
      console.error('Error marking document as reviewed:', err)
      error.value = err instanceof Error ? err.message : 'Error al marcar como revisado'
      throw err
    }
  }

  const unmarkDocumentReviewed = async (documentId: string, purchaseOrderId: string) => {
    try {
      const companyId = companyStore.selectedCompanyId
      if (!companyId) throw new Error('No company selected')

      const docRef = doc(db, `Companies/${companyId}/purchaseOrders/${purchaseOrderId}/Documents/${documentId}`)
      await updateDoc(docRef, { reviewedBy: null })

      const docIndex = documents.value.findIndex(d => d.id === documentId)
      if (docIndex !== -1) {
        documents.value[docIndex].reviewedBy = null
      }
    } catch (err) {
      console.error('Error unmarking document review:', err)
      error.value = err instanceof Error ? err.message : 'Error al desmarcar revisión'
      throw err
    }
  }

  return {
    documents,
    isLoading,
    isUploading,
    uploadProgress,
    error,
    loadDocuments,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    updateDocumentType,
    moveDocumentToFolder,
    copyDocumentsToChildren,
    markDocumentReviewed,
    unmarkDocumentReviewed,
    clearProgress
  }
}
